import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { useSEO } from "@/hooks/useSEO";

export default function ResetPassword() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  useSEO({
    title: lang === "es" ? "Restablecer contraseña — ChefSync P&L" : "Reset password — ChefSync P&L",
    description: lang === "es"
      ? "Introduce tu nueva contraseña para reactivar tu acceso a ChefSync P&L."
      : "Enter your new password to regain access to ChefSync P&L.",
    noIndex: true,
  });
  const [params] = useSearchParams();
  const [token, setToken] = useState(params.get("token") || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = params.get("token");
    if (t) setToken(t);
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success(lang === "es" ? "Contraseña restablecida" : "Password reset");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[var(--background)]">
      <div className="w-full max-w-md bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-8">
        <Link to="/login" className="flex items-center gap-2 mb-8">
          <Logo size={32} />
          <span className="font-display font-bold">ChefSync <span className="text-[var(--text-muted)] font-normal">P&amp;L</span></span>
        </Link>

        <h1 className="font-display text-3xl font-bold tracking-tight mb-6">{t("resetTitle")}</h1>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">{t("resetToken")}</Label>
            <Input
              value={token}
              onChange={(e) => setToken(e.target.value)}
              required
              data-testid="reset-token-input"
              className="h-11 font-mono text-xs"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">{t("newPassword")}</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              data-testid="reset-password-input"
              className="h-11"
              placeholder="••••••••"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="reset-submit"
            className="w-full h-11 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
          >
            {loading ? "..." : t("resetSubmit")}
          </Button>
        </form>
      </div>
    </div>
  );
}
